import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, X, Download, Star, Check } from 'lucide-react';
import { weirdStyles, weirdCategories } from '../data/styles';
import { WEIRD_T } from '../data/generatorsI18n';
import { applyCase, reverseText, spaceText, alternate } from '../lib/unicode';
import { copyText, downloadFile, textStats } from '../lib/helpers';
import { PlatformMock } from '../components/tool/ToolExtras';
import Faq from '../components/Faq';

const MAX = 200;

export default function WeirdTextPage({ lang = 'en' }) {
  const L = WEIRD_T[lang];
  const [input, setRawInput] = useState(L.sample);
  const setInput = (value) => setRawInput(Array.from(value).slice(0, MAX).join(''));
  const [caseMode, setCaseMode] = useState('none');
  const [cat, setCat] = useState('all');
  const [activeId, setActiveId] = useState('chaos');
  const [platform, setPlatform] = useState('Instagram');
  const [copied, setCopied] = useState(null);

  useEffect(() => { setRawInput(L.sample); }, [L]);

  const text = Array.from(applyCase(input, caseMode)).slice(0, MAX).join('');
  const shown = useMemo(() => (cat === 'all' ? weirdStyles : weirdStyles.filter((s) => s.cat === cat)), [cat]);
  const active = weirdStyles.find((s) => s.id === activeId);
  const out = text ? active.fn(text) : '';
  const st = textStats(input);
  const avg = st.words ? (input.replace(/\s/g, '').length / st.words).toFixed(1) : 0;
  const quickFns = [(s) => s.toUpperCase(), (s) => s.toLowerCase(), alternate, reverseText, spaceText];

  const doCopy = async (s) => {
    if (!await copyText(s.fn(text), L.copiedToast)) return;
    setCopied(s.id); setTimeout(() => setCopied(null), 1300);
  };

  return (
    <div data-testid={`weird-page-${lang}`}>
      <section className="content-narrow pt-8 pb-5 text-center fade-up">
        <h1 className="font-hand text-[30px] md:text-[38px] leading-[1.35] text-slate-900" data-testid="page-title">{L.h1}</h1>
        <p className="text-[15px] text-slate-600 mt-2">{L.sub}</p>
      </section>

      <div className="tool max-w-[900px] mx-auto px-4 space-y-3">
        <div className="tool-card cream p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h2 className="font-garamond text-[22px] font-semibold text-[var(--ink)]">{L.converter}</h2>
            <div className="flex flex-wrap gap-1.5">
              {L.cases.map(([k, l]) => (
                <button key={k} className={`pill-btn ${caseMode === k ? 'active' : ''}`} onClick={() => setCaseMode(k)} data-testid={`weird-case-${k}`}>{l}</button>
              ))}
              <button className="sm-btn inline-flex items-center gap-1" onClick={() => setInput('')} data-testid="weird-clear"><X className="w-3 h-3" />{L.clear}</button>
            </div>
          </div>
          <label className="upper-label">{L.yourText}</label>
          <textarea aria-label={L.yourText} value={input} onChange={(e) => setInput(e.target.value)} placeholder={L.placeholder}
            className="w-full h-[90px] mt-1.5 rounded-md border border-[var(--line)] bg-white px-3 py-2.5 text-[15px] resize-y focus:border-[var(--gold2)]" data-testid="weird-input" />
          <div className="flex justify-between text-[11px] text-[var(--ink4)] mt-1"><span>{Array.from(input).length} / {MAX} {L.chars}</span><span>{st.words} {L.words}</span></div>
          <div className="mt-3 rounded-md bg-white border border-[var(--line)] p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="upper-label">{L.outputPreview} <span className="text-[var(--gold)] normal-case tracking-normal ml-1">{active.name}</span></span>
              <button className="btn-gold inline-flex items-center gap-1" onClick={() => out && copyText(out, L.copiedToast)} data-testid="weird-main-copy"><Copy className="w-3.5 h-3.5" />{L.copy}</button>
            </div>
            <div className="text-[22px] text-[var(--ink)] min-h-[40px] break-words leading-[1.9]" data-testid="weird-output">{out || '…'}</div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button className="btn-outline" onClick={() => text && copyText(weirdStyles.map((s) => s.fn(text)).join('\n'), L.copiedToast)} data-testid="weird-copy-all">{L.copyAll}</button>
            <button className="btn-outline inline-flex items-center gap-1" onClick={() => text && downloadFile('weird-text.txt', weirdStyles.map((s) => `${s.name}: ${s.fn(text)}`).join('\n'))} data-testid="weird-export"><Download className="w-3.5 h-3.5" />{L.export}</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {weirdCategories.map((c) => (
            <button key={c.id} className={`pill-btn ${cat === c.id ? 'active' : ''}`} onClick={() => setCat(c.id)} data-testid={`weird-cat-${c.id}`}>{L.cats[c.id]}</button>
          ))}
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
          {shown.map((s) => (
            <li key={s.id} className={`spill !p-3.5 ${activeId === s.id ? 'active' : ''}`} data-testid={`weird-style-${s.id}`}>
              <button type="button" aria-pressed={activeId === s.id} onClick={() => setActiveId(s.id)} className="block w-full text-left" data-testid={`weird-select-${s.id}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-[13px] font-semibold text-[var(--ink)] flex items-center gap-1"><Star className="w-3 h-3 text-[var(--gold2)]" />{s.name}</h3>
                <span className="text-[9.5px] uppercase tracking-wider text-[var(--ink4)]">{L.cats[s.cat]}</span>
              </div>
              <div className="text-[17px] text-[var(--ink)] my-2.5 truncate leading-[1.8]">{s.fn(text || L.sample)}</div>
              </button>
              <button className={`copy-btn inline-flex items-center gap-1 ${copied === s.id ? 'done' : ''}`} onClick={(e) => { e.stopPropagation(); doCopy(s); }} data-testid={`weird-copy-${s.id}`}>
                {copied === s.id ? <><Check className="w-3 h-3" />{L.copied}</> : L.copy}
              </button>
            </li>
          ))}
        </ul>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="tool-card p-3.5">
            <h3 className="upper-label mb-3">{L.quick}</h3>
            <div className="flex flex-wrap gap-1.5">
              {L.quickBtns.map((l, i) => (
                <button key={l} className="pill-btn" onClick={() => setInput(quickFns[i](input))} data-testid={`weird-quick-${i}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="tool-card p-3.5">
            <h3 className="upper-label mb-3">{L.stats}</h3>
            <div className="grid grid-cols-2 gap-2">
              {[st.chars, st.words, st.sentences, avg].map((v, i) => (
                <div key={i} className="stat-box"><div className="stat-val">{v}</div><div className="upper-label !text-[9px]">{L.statLabels[i]}</div></div>
              ))}
            </div>
          </div>
          <div className="tool-card p-3.5">
            <h3 className="upper-label mb-3">{L.platform}</h3>
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {['Instagram', 'Twitter', 'TikTok', 'Discord'].map((p) => (
                <button key={p} className={`pill-btn ${platform === p ? 'active' : ''}`} onClick={() => setPlatform(p)}>{p}</button>
              ))}
            </div>
            <PlatformMock platform={platform} text={out} sample="Bio" />
          </div>
        </div>
      </div>

      <section className="content-narrow py-12 prose-site">
        <h2>{L.howTitle}</h2>
        <ol className="list-decimal pl-6 space-y-2 text-[15px]">
          {L.how.map(([b, d]) => <li key={b}><b>{b}</b> {d}</li>)}
        </ol>
        <h2>{L.whatTitle}</h2>
        <p>{L.what[0]}</p>
        <p>{L.what[1]} {L.prettier} <Link to={L.home}>{L.prettierLink}</Link>.</p>
        <h2>{L.stylesTitle}</h2>
        {weirdCategories.slice(1).map((c) => (
          <div key={c.id}>
            <h3>{L.cats[c.id]}</h3>
            <ul>{weirdStyles.filter((s) => s.cat === c.id).map((s) => <li key={s.id}><b>{s.name}</b> — {s.fn('Sample')}</li>)}</ul>
          </div>
        ))}
        <h2>{L.whyTitle}</h2>
        <ul>{L.why.map(([b, d]) => <li key={b}><b>{b}</b> {d}</li>)}</ul>
      </section>
      <Faq items={L.faq} title={L.faqTitle} />
    </div>
  );
}
